'use client'

/**
 * Shared client-side watchlist store — one source of truth so every <WatchStar> across the site
 * (stock hub, screener, command palette, …) stays in sync from a SINGLE fetch, with optimistic
 * toggles. Backed by useSyncExternalStore. Persists via POST /api/user/watchlist (replaces the full
 * array; order = newest-first, matching the left pane) and broadcasts `watchlist-changed` so the
 * left pane reloads.
 */

import { useSyncExternalStore } from 'react'

const EMPTY: string[] = []
let list: string[] = EMPTY
let loaded = false
let loadingPromise: Promise<void> | null = null
let selfWrite = false
let syncInstalled = false
const listeners = new Set<() => void>()

function emit() { for (const l of listeners) l() }

function fetchList() {
  loadingPromise = fetch('/api/user/watchlist')
    .then((r) => (r.ok ? r.json() : null))
    .then((j) => {
      const arr = Array.isArray(j?.watchlist) ? j.watchlist : []
      list = arr.map((s: unknown) => String(s).toUpperCase())
      loaded = true
      emit()
    })
    .catch(() => {})
    .finally(() => { loadingPromise = null })
}

// Keep in sync with mutations made elsewhere (e.g. the left-pane watchlist), but ignore our own writes.
function onExternalChange() {
  if (selfWrite) { selfWrite = false; return }
  if (!loadingPromise) fetchList()
}

function ensureLoaded() {
  if (typeof window === 'undefined') return
  if (!syncInstalled) { syncInstalled = true; window.addEventListener('watchlist-changed', onExternalChange) }
  if (loaded || loadingPromise) return
  fetchList()
}

function subscribe(cb: () => void) {
  listeners.add(cb)
  ensureLoaded()
  return () => { listeners.delete(cb) }
}

/** Reactive: is this symbol on the user's watchlist? */
export function useWatched(symbol: string): boolean {
  const cur = useSyncExternalStore(subscribe, () => list, () => EMPTY)
  return cur.includes((symbol || '').toUpperCase())
}

/** Add many symbols in ONE persist (for "watch all" from a scan). Returns how many were newly added. */
export async function addManyWatched(symbols: string[]): Promise<number> {
  const clean = symbols.map((s) => (s || '').toUpperCase().replace(/[^A-Z0-9.\-]/g, '')).filter(Boolean)
  const prev = list
  const seen = new Set(prev)
  const added = clean.filter((s) => !seen.has(s))
  if (!added.length) return 0
  list = [...added, ...prev] // newest first
  emit()
  try {
    await fetch('/api/user/watchlist', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ watchlist: list }),
    })
    if (typeof window !== 'undefined') { selfWrite = true; window.dispatchEvent(new Event('watchlist-changed')) }
  } catch {
    list = prev
    emit()
    return 0
  }
  return added.length
}

/** Optimistically add/remove a symbol and persist. Returns the new watched state. */
export async function setWatched(symbol: string, watched: boolean): Promise<boolean> {
  const s = (symbol || '').toUpperCase().replace(/[^A-Z0-9.\-]/g, '')
  if (!s) return false
  const prev = list
  list = watched ? [s, ...prev.filter((x) => x !== s)] : prev.filter((x) => x !== s)
  emit()
  try {
    await fetch('/api/user/watchlist', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ watchlist: list }),
    })
    if (typeof window !== 'undefined') { selfWrite = true; window.dispatchEvent(new Event('watchlist-changed')) }
  } catch {
    list = prev // revert on failure
    emit()
  }
  return watched
}
